import React, { FC, useMemo } from "react";
import { Grid, Tooltip } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { Vessel } from "../types/Vessel";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { sideWeights } from "../../utils/vesselWeightHelpers";
import "../../../styles/vesselstability.css";

export type Side = "port" | "starboard";

interface Props {
  vessel: Vessel;
  containerWeights: number[];
  side: Side;
}

const DEFAULT_BEAM_WARNINGS = [20, 35, 50, 60];

const blink = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const BlinkedBox = styled("div")({
  width: 14,
  height: 14,
  borderRadius: 14,
  animation: `${blink} 1s linear infinite`,
});

interface CheckerResult {
  isPortSideHeavier: boolean;
  overTheLimit: boolean;
  beamWarnings: number[];
  weightDistribution: boolean[];
}

function sideWeightChecker(
  vessel: Vessel,
  containerWeights: number[]
): CheckerResult {
  const { port, starboard } = sideWeights(containerWeights);
  const beamWarnings =
    vessel?.beamWarnings?.length > 0
      ? vessel.beamWarnings
      : DEFAULT_BEAM_WARNINGS;

  const diff = Math.abs(starboard - port);
  const isPortSideHeavier = port >= starboard;
  const overTheLimit = diff > beamWarnings[beamWarnings.length - 1];

  const weightDistribution = beamWarnings.map((thr) => diff >= thr);

  return { isPortSideHeavier, overTheLimit, beamWarnings, weightDistribution };
}

const SideWeightIndicator: FC<Props> = ({
  vessel,
  containerWeights,
  side,
}) => {
  const { isPortSideHeavier, overTheLimit, beamWarnings, weightDistribution } =
    useMemo(() => sideWeightChecker(vessel, containerWeights), [
      vessel,
      containerWeights,
    ]);

  const isThisSideHeavier =
    side === "port" ? isPortSideHeavier : !isPortSideHeavier;

  const baseClass =
    side === "port" ? "side-weight side-weight-port" : "side-weight side-weight-starboard";

  return (
    <Grid className={`side-weights side-weights--${side}`} item flexDirection={`${ side === "port" ? 'row-reverse' : 'row'}`}>
      {weightDistribution.map((flag, idx) => {
        const threshold = beamWarnings?.[3 - idx] ?? 0;
        const active = flag && isThisSideHeavier;
        const showBlinked = active && overTheLimit && idx === 3;

        return (
          <Tooltip key={idx} title={`${threshold}T`}>
            {showBlinked ? (
              <BlinkedBox className={`${baseClass}--active`} />
            ) : (
              <CircleIcon
                fontSize="small"
                className={`${baseClass} ${active ? `${baseClass}--active` : ""}`}
              />
            )}
          </Tooltip>
        );
      })}
    </Grid>
  );
};

export default SideWeightIndicator;